import { useEffect } from "react";
import PlatformBrand from "../PlatformBrand";
import { usePortal } from "./usePortal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const brandLogoSrc = (branding) => {
  const p = branding?.logo_public_path;
  if (!p) return "";
  return p.startsWith("http") ? p : `${BACKEND}${p}`;
};

export function useBrandName() {
  const { me } = usePortal();
  return me?.branding?.company_name || "Facilities Management Platform";
}

/* Wording for an assigned-but-unnamed contractor: clients with their own branding see "<Brand> team", others the generic copy. */
export function useTeamLabel() {
  const { me } = usePortal();
  const brand = me?.branding?.company_name || "";
  return {
    brand,
    assigned: brand ? `${brand} team` : "Contractor assigned",
    dispatched: brand ? `${brand} team dispatched` : "Contractor assigned and dispatched",
    tbc: brand ? "Team TBC" : "Contractor TBC",
    blurb: brand ? `A member of the ${brand} team has been allocated to this job.` : "A vetted contractor from our network has been allocated to this job.",
    heading: brand ? "Team" : "Contractor",
  };
}

/* Sidebar / mobile header identity for client users — falls back to the platform mark when the client has no branding. */
export function PortalBrand({ collapsed = false, mobile = false }) {
  const { me } = usePortal();
  const b = me?.branding;
  const name = b?.company_name || "Facilities Management Platform";
  const logo = brandLogoSrc(b);

  useEffect(() => {
    if (!me) return;
    const base = `${name} — Client Portal`;
    const m = document.title.match(/^\(\d+\+?\)\s*/);
    document.title = m ? `${m[0]}${base}` : base;
  }, [me, name]);

  if (!b) {
    return <PlatformBrand variant={mobile ? "mobile" : collapsed ? "icon" : "sidebar"} />;
  }

  const initial = <span className="h-8 w-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold font-[Chivo] shrink-0" data-testid="portal-brand-initial">{name.charAt(0).toUpperCase()}</span>;
  if (collapsed) return logo ? <img src={logo} alt={name} className="h-8 w-8 object-contain" data-testid="portal-brand-logo-icon" /> : initial;
  return (
    <div className={`flex items-center gap-2 min-w-0 ${mobile ? "" : "w-full"}`} data-testid="portal-brand">
      {logo ? <img src={logo} alt={name} className={`${mobile ? "h-6 max-w-[90px]" : "h-8 max-w-[96px]"} w-auto object-contain shrink-0`} data-testid="portal-brand-logo" /> : initial}
      <span className={`font-bold font-[Chivo] truncate leading-tight ${mobile ? "text-[13px]" : "text-xs"}`} data-testid="portal-brand-name">{name}</span>
    </div>
  );
}
